import * as api from '@gitbook/api-client';
import { createPalette, PaletteButton, type PaletteCommandDef } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { type ButtonProps, DialogRoot, useOverlayTriggerState } from '@gitbook/spine-react';

import { type FeatureUpgradeAction, useTrialingSiteFeaturePlan } from '../../../state';
import { getPaidIndicatorBadgeProps } from '../PaidFeatureIndicator';
import { CreateSiteExternalLinkDialog } from './CreateSiteExternalLinkDialog';
import type { CreateSiteExternalLinkFormProps } from './CreateSiteExternalLinkForm';
import {
    CreateSiteSectionDialog,
    type CreateSiteSectionDialogProps,
} from './CreateSiteSectionDialog';
import {
    CreateSiteSectionGroupDialog,
    type CreateSiteSectionGroupDialogProps,
} from './CreateSiteSectionGroupDialog';
import {
    CreateSiteVariantDialog,
    type CreateSiteVariantDialogProps,
} from './CreateSiteVariantDialog';

interface CreateRootSiteItemButtonProps
    extends
        CreateSiteSectionDialogProps,
        Omit<CreateSiteVariantDialogProps, 'siteSection'>,
        Omit<CreateSiteSectionGroupDialogProps, 'siteSections'>,
        Omit<CreateSiteExternalLinkFormProps, 'parent'>,
        Pick<ButtonProps, 'className' | 'label' | 'size' | 'iconOnly' | 'kind'> {
    upgrade: FeatureUpgradeAction | undefined;
    siteStructure: api.SiteStructure;
    canAdminSite: boolean;
}

export function CreateRootSiteItemButton(props: CreateRootSiteItemButtonProps) {
    const {
        upgrade,
        siteStructure,
        organization,
        site,
        onCreateSection,
        onCreateSectionGroup,
        onCreateVariant,
        onCreateExternalLink,
        canAdminSite,
        ...buttonProps
    } = props;
    const createSiteSectionGroupDialogTrigger = useOverlayTriggerState();
    const createSiteSectionDialogTrigger = useOverlayTriggerState();
    const createSiteSectionVariantTrigger = useOverlayTriggerState();
    const createSiteExternalLinkTrigger = useOverlayTriggerState();
    const trialingPlan = useTrialingSiteFeaturePlan(site, api.SiteFeatureKey.SitesSections);
    const canAuthorExternalLinks = canAdminSite;
    const createExternalLinkCommand = getCreateExternalLinkCommand({
        canAuthorExternalLinks,
        upgrade,
        trialingPlan,
        trigger: createSiteExternalLinkTrigger.open,
    });
    return (
        <>
            <DialogRoot {...createSiteSectionGroupDialogTrigger.rootProps}>
                <CreateSiteSectionGroupDialog
                    organization={organization}
                    site={site}
                    siteSections={[]}
                    onCreateSectionGroup={onCreateSectionGroup}
                />
            </DialogRoot>
            <DialogRoot {...createSiteSectionDialogTrigger.rootProps}>
                <CreateSiteSectionDialog
                    organization={organization}
                    site={site}
                    siteStructure={siteStructure}
                    onCreateSection={onCreateSection}
                />
            </DialogRoot>
            <DialogRoot {...createSiteSectionVariantTrigger.rootProps}>
                <CreateSiteVariantDialog
                    organization={organization}
                    site={site}
                    siteStructure={siteStructure}
                    siteSection={null}
                    onCreateVariant={onCreateVariant}
                />
            </DialogRoot>
            <DialogRoot {...createSiteExternalLinkTrigger.rootProps}>
                <CreateSiteExternalLinkDialog
                    organization={organization}
                    site={site}
                    siteStructure={siteStructure}
                    onCreateExternalLink={onCreateExternalLink}
                />
            </DialogRoot>
            <PaletteButton
                {...buttonProps}
                leadingIcon={Icon.Plus}
                palette={() =>
                    createPalette({
                        groups: [
                            {
                                key: 'add',
                                commands: [
                                    siteStructure.type === 'siteSpaces'
                                        ? {
                                              key: 'addVariant',
                                              title: 'Variant',
                                              icon: Icon.Plus,
                                              trigger: createSiteSectionVariantTrigger.open,
                                          }
                                        : null,
                                    {
                                        key: 'addSection',
                                        title: 'Section',
                                        icon: Icon.AddSiteSection,
                                        trigger: upgrade
                                            ? upgrade.onPress
                                            : createSiteSectionDialogTrigger.open,
                                        upgrade: Boolean(upgrade),
                                        badge: getPaidIndicatorBadgeProps({
                                            upgrade,
                                            trialingPlan,
                                        }),
                                        disabled:
                                            !upgrade &&
                                            siteStructure.type === 'siteSpaces' &&
                                            !canAdminSite,
                                        subtitle:
                                            !upgrade &&
                                            siteStructure.type === 'siteSpaces' &&
                                            !canAdminSite
                                                ? 'Only site administrators can enable sections.'
                                                : undefined,
                                    },
                                    {
                                        key: 'addSectionGroup',
                                        title: 'Group',
                                        icon: Icon.AddSiteGroup,
                                        trigger: upgrade
                                            ? upgrade.onPress
                                            : createSiteSectionGroupDialogTrigger.open,
                                        upgrade: Boolean(upgrade),
                                        badge: getPaidIndicatorBadgeProps({
                                            upgrade,
                                            trialingPlan,
                                        }),
                                        disabled:
                                            !upgrade &&
                                            siteStructure.type === 'siteSpaces' &&
                                            !canAdminSite,
                                        subtitle:
                                            !upgrade &&
                                            siteStructure.type === 'siteSpaces' &&
                                            !canAdminSite
                                                ? 'Only site administrators can enable sections.'
                                                : undefined,
                                    },
                                    createExternalLinkCommand,
                                ].filter((x) => x !== null),
                            },
                        ],
                    })
                }
            />
        </>
    );
}

function getCreateExternalLinkCommand(args: {
    canAuthorExternalLinks: boolean;
    upgrade: FeatureUpgradeAction | undefined;
    trialingPlan?: string | null;
    trigger: () => void;
}): PaletteCommandDef | null {
    const { canAuthorExternalLinks, upgrade, trialingPlan, trigger } = args;
    if (!canAuthorExternalLinks) {
        return null;
    }

    return {
        key: 'addExternalLink',
        title: 'External link',
        icon: Icon.Link,
        trigger: upgrade?.onPress ?? trigger,
        upgrade: Boolean(upgrade),
        badge: getPaidIndicatorBadgeProps({ upgrade, trialingPlan }),
    };
}
